import { fetchAuthSession, fetchUserAttributes } from 'aws-amplify/auth';

import { useGoogleLogin } from '@react-oauth/google';
import { CognitoGroups, SpecterXUser } from '../../types/types';
import { OAuthUserAttributes } from './interfaces';
import { captureErrorForSentry, safeSplit } from '../../components/utils';
import { IAuthUser } from '@/services/IAuthService';

export const useGoogleLoginService = (responseSuccess, responseFail) => useGoogleLogin({
    flow: 'auth-code',
    scope: 'openid https://www.googleapis.com/auth/userinfo.profile '
        + 'openid https://www.googleapis.com/auth/userinfo.email '
        + 'https://www.googleapis.com/auth/drive.install '
        + 'https://www.googleapis.com/auth/drive.file',
    onSuccess: async (codeResponse) => {
        responseSuccess({ code: codeResponse.code });
    },
    onError: (errorResponse) => responseFail(new Error(errorResponse.error)),
});

export const getWSOGroups = (groups: string): CognitoGroups[] => safeSplit<CognitoGroups>(groups, ',');

/**
 * Type guard: was this user produced by the IdP path (WSO2 OIDC / Kerberos)?
 *
 * Cannot be inferred from IS_WSO alone — in WSO/Kerberos environments,
 * external recipients on routes like /shared-file authenticate via Cognito
 * OTP and arrive here as CognitoUser, not IAuthUser. We must inspect the
 * actual shape (`profile` is only present on IAuthUser) before reading
 * `profile.email` and friends, otherwise CognitoUser would throw.
 */
export const isIdpAuthUser = (user: SpecterXUser, isWso: boolean): user is IAuthUser =>
    isWso && !!(user as IAuthUser)?.profile;

export const getUserAttributes = async (user: SpecterXUser, IS_WSO: boolean): Promise<OAuthUserAttributes> => {
    if (isIdpAuthUser(user, IS_WSO)) {
        const { profile } = user;

        const email = profile.email || '';
        const upn = profile.upn || '';
        const username = upn ? upn.split('@')[0] : (email.split('@')[0] || '');

        return {
            sub: profile.sub,
            email: email,
            given_name: profile.given_name || username,
            family_name: profile.family_name || '',
            preferred_username: profile.preferred_username || email || upn,
            groups: profile.groups || '',
        };
    }
    // v6: attributes come from the token/userInfo endpoint, not a user object.
    return fetchUserAttributes() as Promise<OAuthUserAttributes>;
};

export const tryGetCognitoGroups = async (): Promise<CognitoGroups[]> => {
    const groupsList: CognitoGroups[] = [];
    try {
        const { tokens } = await fetchAuthSession();
        const groups = (tokens?.idToken?.payload?.['cognito:groups'] as CognitoGroups[]) || [];
        groupsList.push(...groups);
    } catch (error) {
        console.log('Failed to parse cognito groups', error);
        captureErrorForSentry(error, 'UserStore.tryGetCognitoGroups');
    }
    return groupsList;
};
